import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomerAddress } from "../types/address";
import { borderRadius, typography, elevation } from "../theme";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onSave: (address: CustomerAddress) => void;
  address?: CustomerAddress;
}

export const AddressModal: React.FC<Props> = ({
  isVisible,
  onClose,
  onSave,
  address,
}) => {
  const defaultAddress: CustomerAddress = useMemo(() => ({
    externalId: crypto.randomUUID(),
    isActive: true,
    isPrimary: false,
    addressType: "Home",
    nameNumber: "",
    addressShortName: "",
    addressName: "",
    street: "",
    town: "",
    postcode: "",
    //   buildingId: "",
    organisation: "",
    department: "",
    district: "",
    county: "",
    country: "",
  }), []);

  const [formData, setFormData] = useState<CustomerAddress>(
    address?.externalId ? address : defaultAddress
  );

  useEffect(() => {
    if (address?.externalId) {
      setFormData(address);
    }

    return () => {
      setFormData({ 
        externalId: crypto.randomUUID(),
        isActive: true,
        isPrimary: false,
        addressType: "Home",
        nameNumber: "",
        addressShortName: "",
        addressName: "",
        street: "",
        town: "",
        postcode: "",
        organisation: "",
        department: "",
        district: "",
        county: "",
        country: "",
      }); // new ID on reset
    };
  }, [address]);
  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | {
          target: {
            name: string;
            value: string | boolean;
            type?: string;
            checked?: boolean;
          };
        }
  ) => {
    const target = e.target;
    const name = target.name;
    const isCheckbox = target.type === "checkbox";
    const value = isCheckbox
      ? !!(target as { checked?: boolean }).checked
      : target.value;

    if (!name) return;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog
      open={isVisible}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: borderRadius.small,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          sx={{
            fontFamily: typography.fontFamily.primary,
            fontSize: typography.fontSize.xxl,
            fontStyle: "normal",
            fontWeight: typography.fontWeight.bold,
            lineHeight: typography.lineHeight.relaxed,
            mt: "20px",
            ml: "20px",
          }}
        >
          {address?.externalId ? "Edit Address" : "New Address"}
        </DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{ height: "40px", mt: "24px", mr: "32px" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent dividers>
        <Paper sx={{ p: 3, height: "100%", m: "20px" }} elevation={elevation.paper}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            display="grid"
            gap={3}
            gridTemplateColumns="repeat(12, 1fr)"
          >
            <Typography
              gridColumn={{
                xs: "span 12",
                sm: "span 12",
              }}
              sx={{
                fontFamily: typography.fontFamily.primary,
                fontSize: typography.fontSize.xxl,
                fontStyle: "normal",
                fontWeight: typography.fontWeight.normal,
                lineHeight: typography.lineHeight.relaxed,
              }}
            >
              Introduce your Address
            </Typography>

            <Box gridColumn={{ xs: "span 12", sm: "span 2" }}>
              <TextField
                fullWidth
                disabled
                label="Type"
                name="addressType"
                value={formData.addressType}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={{ xs: "span 12", sm: "span 4" }}>
              <TextField
                fullWidth
                label="Number"
                name="nameNumber"
                value={formData.nameNumber}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={{ xs: "span 12", sm: "span 6" }}>
              <TextField
                fullWidth
                label="Street"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={{ xs: "span 12", sm: "span 3" }}>
              <TextField
                fullWidth
                label="District"
                name="district"
                value={formData.district}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={{ xs: "span 12", sm: "span 3" }}>
              <TextField
                fullWidth
                label="Town"
                name="town"
                value={formData.town}
                onChange={handleInputChange}
              />
            </Box>            

            <Box gridColumn={{ xs: "span 12", sm: "span 3" }}>
              <TextField
                fullWidth
                required
                label="Post Code"
                name="postcode"
                value={formData.postcode}
                onChange={handleInputChange}
              />
            </Box>
            <Box
              gridColumn={{ xs: "span 12", sm: "span 3" }}
              sx={{ display: "flex", flexDirection: "column", mt: "3px" }}
              gap={1}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPrimary}
                    onChange={handleInputChange}
                    name="isPrimary"
                  />
                }
                label={<Typography variant="caption">Primary</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                  />
                }
                label={<Typography variant="caption">Active</Typography>}
              />
            </Box>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          gap: "2",
          mr: "34px",
          ml: "34px",
          mb: "20px",
          mt: "10px",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => onClose()}
          fullWidth
          sx={{
            borderColor: "black",
            color: "black",
            fontWeight: "bold",
            fontFamily: "Monsterrat",
            borderWidth: 2,
            textTransform: "none",
            "&:hover": {
              borderColor: "black",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(formData)}
          fullWidth
          sx={{
            fontWeight: "bold",
            textTransform: "none",
            fontFamily: "Monsterrat",
          }}
        >
          Confirm Address
        </Button>
      </DialogActions>
    </Dialog>
  );
};
